// CreateNewSupplierInvoice.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from '../../../../services/AxiosInstance';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Alert, Card, Spinner, Button, Badge, Form, Row, Col } from 'react-bootstrap';
import FileDropZone from '../../SharedComponents/FileDropZone';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { format } from 'date-fns';
import NewSupplierModal from '../Factoring/Suppliers/NewSupplierModal';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import swal from 'sweetalert';
import PageTitle from '../../../layouts/PageTitle';

// Lucide React Icons
import {
  Upload,
  FileText,
  Building,
  Calendar,
  DollarSign,
  Check,
  AlertCircle,
  Info,
  X,
  Eye,
  Plus,
  Loader2,
  Brain,
  Camera,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';

const CreateNewSupplierInvoice = () => {
  const { companyId, invoiceId } = useParams();
  const history = useHistory();

  // Edit mode detection
  const isEditMode = Boolean(invoiceId);

  // State variables - Enhanced for modern workflow
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date());
  const [dueDate, setDueDate] = useState(new Date());
  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState('Scanned');
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showNewSupplierModal, setShowNewSupplierModal] = useState(false);
  const [initialSupplierData, setInitialSupplierData] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showInfo, setShowInfo] = useState(true);
  const [modalKey, setModalKey] = useState(0);
  const [loadingInvoice, setLoadingInvoice] = useState(isEditMode);

  // Modern workflow state
  const [currentStep, setCurrentStep] = useState(isEditMode ? 2 : 1); // Skip upload step in edit mode
  const [ocrData, setOcrData] = useState(null);
  const [fieldValidation, setFieldValidation] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [showSupplierSuggestions, setShowSupplierSuggestions] = useState(false);

  // Modern helper functions
  const getStepStatus = (step) => {
    if (step < currentStep) return 'completed';
    if (step === currentStep) return 'current';
    return 'pending';
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return '#059669'; // Green
    if (confidence >= 0.6) return '#d97706'; // Orange
    return '#dc2626'; // Red
  };

  const getConfidenceText = (confidence) => {
    if (confidence >= 0.8) return 'Hög tillförlitlighet';
    if (confidence >= 0.6) return 'Medelhög tillförlitlighet';
    return 'Låg tillförlitlighet - kontrollera';
  };

  const validateField = useCallback((fieldName, value, confidence = 1) => {
    const validation = { isValid: true, message: '', confidence };

    switch (fieldName) {
      case 'invoiceNumber':
        if (!value || value.trim() === '') {
          validation.isValid = false;
          validation.message = 'Fakturanummer krävs';
        }
        break;
      case 'amount':
        if (!value || isNaN(parseFloat(value)) || parseFloat(value) <= 0) {
          validation.isValid = false;
          validation.message = 'Giltigt belopp krävs';
        }
        break;
      case 'supplier':
        if (!value || value === '') {
          validation.isValid = false;
          validation.message = 'Leverantör måste väljas';
        }
        break;
      default:
        break;
    }

    setFieldValidation(prev => ({
      ...prev,
      [fieldName]: validation
    }));

    return validation;
  }, []);

  // Re-validate supplier field when selection changes
  useEffect(() => {
    if (currentStep === 2 && selectedSupplier) {
      validateField('supplier', selectedSupplier);
    }
  }, [selectedSupplier, currentStep, validateField]);

  const handleClose = () => {
    setShowNewSupplierModal(false);
    // We don't immediately reset initialSupplierData here to allow the modal to clean up
  };

  const handleSave = (newSupplierData) => {
    saveNewSupplier(newSupplierData);
    setShowNewSupplierModal(false);
  };

  const openNewSupplierModal = (initialData = null) => {
    console.log('Opening new supplier modal with initial data:', initialData);
    setInitialSupplierData(initialData);
    setModalKey(prevKey => prevKey + 1); // Increment key to force re-render
    setShowNewSupplierModal(true);
  };

  const saveNewSupplier = (newData) => {
    const newSupplier = {
      name: newData.name,
      orgnumber: newData.orgnumber, // Updated to match the correct field name
      vatnumber: newData.vatnumber, // Updated to match the correct field name
      postadress1: newData.address,
      postadress2: '',
      city: newData.city,
      postnumber: newData.postalcode, // Updated to match the correct field name
      country: newData.country,
      phonenumber: newData.phonenumber, // Updated to match the correct field name
    };

    console.log('New supplier data:', newSupplier);

    setIsProcessing(true);
    const createSupplierApiUrl = getApiUrl(`${companyId}/purchasing/suppliers`);
    axios
      .post(createSupplierApiUrl, newSupplier, { withCredentials: true })
      .then((response) => {
        if (response.status === 201) {
          swal('Klart!', 'Leverantören har skapats.', 'success');
          fetchSuppliers().then(() => {
            // Set the newly created supplier as selected after fetch
            if (response.data && response.data.supplier_id) {
              setSelectedSupplier(response.data.supplier_id);
            } else {
              // If supplier_id is not in response, try to find it by name
              const newlyAddedSupplier = suppliers.find(s => s.name === newData.name);
              if (newlyAddedSupplier) {
                setSelectedSupplier(newlyAddedSupplier.supplier_id);
              }
            }
            setError(null);
          });
        } else {
          setError('Kunde inte lägga till leverantören. Vänligen försök igen.');
        }
      })
      .catch((error) => {
        console.error('Error saving supplier:', error);
        setError('Kunde inte lägga till leverantören. Vänligen försök igen.');
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  // Fetch existing invoice data if in edit mode
  const fetchInvoiceData = async () => {
    if (!isEditMode) return;

    try {
      setLoadingInvoice(true);
      const getInvoiceApiUrl = getApiUrl(`${companyId}/purchasing/supplier-invoices/${invoiceId}`);
      const response = await axios.get(getInvoiceApiUrl, { withCredentials: true });
      const invoiceData = response.data;

      // Populate form with existing data
      setSelectedSupplier(invoiceData.supplier_id || '');
      setInvoiceNumber(invoiceData.invoice_number || '');
      setInvoiceDate(invoiceData.invoice_date ? new Date(invoiceData.invoice_date) : new Date());
      setDueDate(invoiceData.due_date ? new Date(invoiceData.due_date) : new Date());
      setAmount(invoiceData.amount?.toString() || '');
      setStatus(invoiceData.status || 'Scanned');

      setLoadingInvoice(false);
    } catch (err) {
      console.error('Error fetching invoice data:', err);
      setError('Kunde inte hämta fakturadata. Vänligen försök igen senare.');
      setLoadingInvoice(false);
    }
  };

  useEffect(() => {
    fetchSuppliers().then(() => {
      // Fetch invoice data after suppliers are loaded (needed for validation)
      if (isEditMode) {
        fetchInvoiceData();
      }
    });
  }, []);

  // Fetch suppliers from the backend
  const fetchSuppliers = async () => {
    try {
      const getSuppliersApiUrl = getApiUrl(`${companyId}/purchasing/suppliers`);
      const result = await axios.get(getSuppliersApiUrl, { withCredentials: true });
      setSuppliers(result.data);
      return result.data;
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError('Kunde inte hämta leverantörer. Vänligen försök igen senare.');
      return [];
    }
  };

  // Handle files uploaded via FileDropZone
  const handleFilesUploaded = async (uploadedFiles) => {
    if (!uploadedFiles || uploadedFiles.length === 0) {
      return;
    }

    setIsProcessing(true);
    setError(null);
    setFiles(uploadedFiles); // Store the files for later use

    try {
      const file = uploadedFiles[0];

      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        getApiUrl(`${companyId}/purchasing/supplier-invoices/analyze-invoice`),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
        }
      );

      const extractedData = response.data;
      console.log('🔍 DEBUG - Extracted OCR data:', extractedData);

      // Store OCR data for confidence display
      setOcrData(extractedData);

      // Update form fields with extracted data and validate
      if (extractedData.invoiceId && extractedData.invoiceId.value) {
        setInvoiceNumber(extractedData.invoiceId.value);
        validateField('invoiceNumber', extractedData.invoiceId.value, extractedData.invoiceId.confidence);
      }

      if (extractedData.invoiceDate && extractedData.invoiceDate.value) {
        setInvoiceDate(new Date(extractedData.invoiceDate.value));
      }

      if (extractedData.dueDate && extractedData.dueDate.value) {
        setDueDate(new Date(extractedData.dueDate.value));
      }

      if (extractedData.invoiceTotal && extractedData.invoiceTotal.amount) {
        setAmount(extractedData.invoiceTotal.amount);
        validateField('amount', extractedData.invoiceTotal.amount, extractedData.invoiceTotal.confidence);
      }

      // Match supplier by name (case-insensitive)
      if (extractedData.vendorName && extractedData.vendorName.value) {
        const vendorName = extractedData.vendorName.value.toLowerCase();
        const matchingSupplier = suppliers.find(
          (supplier) => supplier.name.toLowerCase() === vendorName
        );

        if (matchingSupplier) {
          setSelectedSupplier(matchingSupplier.supplier_id);
          validateField('supplier', matchingSupplier.supplier_id, 1.0);
        } else {
          setError('Leverantören hittades inte. Du kan lägga till en ny leverantör eller välja en befintlig.');

          // Prepare initial supplier data for the modal
          const initialData = {
            name: extractedData.vendorName.value || '',
            address: extractedData.vendorAddress?.text || '',
            orgnumber: extractedData.vendorOrg?.value || '',
            city: extractedData.vendorCity?.value || '',
            postalcode: extractedData.vendorPostal?.value || '',
            country: 'SE', // Default to Sweden
          };

          // Set the initial data for the modal
          setInitialSupplierData(initialData);
          
          // Show supplier suggestions
          setSupplierSearchTerm(extractedData.vendorName.value || '');
          const suggestions = suppliers.filter(s => 
            s.name.toLowerCase().includes(vendorName.substring(0, 3))
          ).slice(0, 3);
          setSupplierSuggestions(suggestions);
        }
      }

      // Move to review step after successful OCR
      setCurrentStep(2);
      setError(null);

      console.log('✅ OCR processing completed, moved to review step');
    } catch (err) {
      console.error('Error uploading and processing invoice:', err);
      setError('Ett fel uppstod vid behandling av fakturan. Vänligen försök igen.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle form submission to create or update invoice
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSupplier) {
      setError('Vänligen välj en leverantör först.');
      return;
    }

    setError(null);
    setSuccessMessage('');
    setIsProcessing(true);

    try {
      const invoiceData = {
        supplier_id: selectedSupplier,
        invoice_number: invoiceNumber,
        invoice_date: format(new Date(invoiceDate), 'yyyy-MM-dd'),
        due_date: format(new Date(dueDate), 'yyyy-MM-dd'),
        amount: parseFloat(amount),
        status,
      };

      let responseInvoiceId;

      if (isEditMode) {
        // Update existing invoice
        const updateInvoiceApiUrl = getApiUrl(`${companyId}/purchasing/supplier-invoices/${invoiceId}`);
        await axios.put(updateInvoiceApiUrl, invoiceData, { withCredentials: true });
        responseInvoiceId = invoiceId;

        await swal({
          title: "Uppdaterad!",
          text: "Fakturan har uppdaterats.",
          icon: "success"
        });
      } else {
        // Create new invoice
        const createInvoiceApiUrl = getApiUrl(`${companyId}/purchasing/supplier-invoices`);
        const response = await axios.post(createInvoiceApiUrl, invoiceData, { withCredentials: true });
        responseInvoiceId = response.data.invoice_id;

        // If files are attached, upload them now (only for new invoices)
        if (files.length > 0) {
          await uploadFiles(responseInvoiceId);
        }

        setSuccessMessage('Fakturan har skapats framgångsrikt!');
      }

      clearForm();
      history.push(`/${companyId}/supplier-invoices`); // Redirect to the supplier invoices list
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} invoice:`, err);
      setError(`Kunde inte ${isEditMode ? 'uppdatera' : 'skapa'} fakturan. Vänligen kontrollera uppgifterna och försök igen.`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Function to clear form fields
  const clearForm = () => {
    setSelectedSupplier('');
    setInvoiceNumber('');
    setInvoiceDate(new Date());
    setDueDate(new Date());
    setAmount('');
    setStatus('Pending');
    setFiles([]);
    setPreviewUrl(null);
    setError(null);
    setSuccessMessage('');
    setInitialSupplierData(null);
  };

  // Function to upload files
  const uploadFiles = async (invoiceId) => {
    try {
      const uploadFileApiUrl = getApiUrl(`${companyId}/purchasing/supplier-invoices/${invoiceId}/attachments`);
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('file', file);
      });

      await axios.post(uploadFileApiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
    } catch (err) {
      console.error('Error uploading files:', err);
      // Handle errors, e.g., inform the user
      setError('Fakturan skapades, men filuppladdningen misslyckades.');
    }
  };

  // Generate preview URL when files change
  useEffect(() => {
    if (files.length > 0) {
      const url = URL.createObjectURL(files[0]);
      setPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
        setPreviewUrl(null);
      };
    } else {
      setPreviewUrl(null);
    }
  }, [files]);

  // Function to remove the uploaded file
  const handleRemoveFile = () => {
    setFiles([]);
    setPreviewUrl(null);
  };

  return (
    <>
      <style>{`
        .modern-card {
          background: white;
          border: none;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .modern-card:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }

        .step-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          font-weight: 600;
          font-size: 0.875rem;
          transition: all 0.3s ease;
        }

        .step-indicator.completed {
          background: #059669;
          color: white;
        }

        .step-indicator.current {
          background: #44814E;
          color: white;
        }

        .step-indicator.pending {
          background: #f3f4f6;
          color: #6b7280;
        }

        .step-connector {
          flex: 1;
          height: 2px;
          background: #e5e7eb;
          margin: 0 1rem;
        }

        .step-connector.completed {
          background: #44814E;
        }

        .confidence-badge {
          display: inline-flex;
          align-items: center;
          padding: 0.25rem 0.5rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 500;
          margin-left: 0.5rem;
        }

        .modern-input {
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          padding: 0.75rem;
          transition: all 0.2s ease;
          font-size: 0.875rem;
        }

        .modern-input:focus {
          border-color: #44814E;
          box-shadow: 0 0 0 3px rgba(68, 129, 78, 0.1);
          outline: none;
        }

        .modern-input.error {
          border-color: #dc2626;
        }

        .field-validation {
          font-size: 0.75rem;
          margin-top: 0.25rem;
        }

        .upload-zone {
          border: 2px dashed #d1d5db;
          border-radius: 12px;
          padding: 3rem 2rem;
          text-align: center;
          transition: all 0.3s ease;
          background: #fafbfc;
        }

        .upload-zone:hover {
          border-color: #44814E;
          background: #f0f9ff;
        }

        .upload-zone.active {
          border-color: #44814E;
          background: #f0f9ff;
        }

        .supplier-suggestion {
          padding: 0.75rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          margin-bottom: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .supplier-suggestion:hover {
          background: #f8fafc;
          border-color: #44814E;
        }

        .ocr-result-field {
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
        }

        @media (max-width: 768px) {
          .step-indicator {
            width: 35px;
            height: 35px;
            font-size: 0.75rem;
          }
          
          .modern-card {
            margin-bottom: 1rem;
          }
        }
      `}</style>

      <PageTitle
        activeMenu={isEditMode ? "Redigera Leverantörsfaktura" : "Skapa Leverantörsfaktura"}
        motherMenu="Fakturor"
      />
      
      <div className="col-12">
        {/* Loading State for Edit Mode */}
        {loadingInvoice && (
          <div className="modern-card mb-4">
            <div className="p-4 text-center">
              <Spinner animation="border" style={{ color: '#44814E' }} className="mb-3" />
              <div style={{ color: '#6b7280' }}>Laddar fakturadata...</div>
            </div>
          </div>
        )}

        {/* Modern Progress Steps */}
        {!loadingInvoice && (
          <>
            <div className="modern-card mb-4">
              <div className="p-4">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className={`step-indicator ${getStepStatus(1)}`}>
                      {getStepStatus(1) === 'completed' ? <Check size={20} /> : '1'}
                    </div>
                    <div className="ms-3">
                      <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>Ladda upp</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Dokument & OCR</div>
                    </div>
                  </div>

                  <div className={`step-connector ${currentStep > 1 ? 'completed' : ''}`}></div>

                  <div className="d-flex align-items-center">
                    <div className={`step-indicator ${getStepStatus(2)}`}>
                      {getStepStatus(2) === 'completed' ? <Check size={20} /> : '2'}
                    </div>
                    <div className="ms-3">
                      <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>Granska</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Validera uppgifter</div>
                    </div>
                  </div>

                  <div className={`step-connector ${currentStep > 2 ? 'completed' : ''}`}></div>

                  <div className="d-flex align-items-center">
                    <div className={`step-indicator ${getStepStatus(3)}`}>
                      {getStepStatus(3) === 'completed' ? <Check size={20} /> : '3'}
                    </div>
                    <div className="ms-3">
                      <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>Skapa</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Slutför faktura</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Error/Success Messages */}
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <AlertCircle size={16} className="me-2" />
            {error}
            {error.includes('Leverantören hittades inte') && (
              <Button 
                variant="outline-danger" 
                size="sm" 
                className="ms-2"
                onClick={() => openNewSupplierModal(initialSupplierData)}
              >
                <Plus size={14} className="me-1" />
                Lägg till leverantör
              </Button>
            )}
          </Alert>
        )}

        {successMessage && (
          <Alert variant="success">
            <CheckCircle2 size={16} className="me-2" />
            {successMessage}
          </Alert>
        )}

        {/* Main Content Area */}
        <Row>
          {/* Step 1: Document Upload */}
          {currentStep === 1 && (
            <Col lg={8}>
              <div className="modern-card">
                <div className="p-4">
                  <div className="d-flex align-items-center mb-4">
                    <Upload size={24} style={{ color: '#44814E' }} className="me-3" />
                    <div>
                      <h4 style={{ margin: 0, fontWeight: '600' }}>Ladda upp leverantörsfaktura</h4>
                      <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                        Släpp din faktura här för automatisk dataextraktion med AI
                      </p>
                    </div>
                  </div>

                  {isProcessing && (
                    <div className="text-center mb-4 p-4" style={{ background: '#f0f9ff', borderRadius: '8px' }}>
                      <Brain size={32} style={{ color: '#44814E' }} className="mb-2" />
                      <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>AI bearbetar dokument</div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        Extraherar leverantörsuppgifter, belopp och datum...
                      </div>
                      <Spinner animation="border" style={{ color: '#44814E' }} className="mt-2" />
                    </div>
                  )}

                  {files.length === 0 && !isProcessing && (
                    <div className="upload-zone">
                      <Upload size={48} style={{ color: '#9ca3af' }} className="mb-3" />
                      <div style={{ fontWeight: '600', fontSize: '1.125rem', marginBottom: '0.5rem' }}>
                        Släpp din faktura här
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
                        eller klicka för att välja fil
                      </div>
                      <FileDropZone
                        setFiles={setFiles}
                        onFilesUploaded={handleFilesUploaded}
                        autoUpload={true}
                        acceptedFileTypes="image/*, application/pdf"
                        mainText=""
                        subText=""
                        multiple={false}
                      />
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '1rem' }}>
                        Stöder PDF och bildformat (JPG, PNG) • Max 10MB
                      </div>
                    </div>
                  )}

                  {files.length > 0 && previewUrl && (
                    <div className="mt-4">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h5 style={{ margin: 0, fontWeight: '600' }}>
                          <Eye size={20} className="me-2" />
                          Förhandsgranskning
                        </h5>
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          onClick={handleRemoveFile}
                        >
                          <X size={16} className="me-1" />
                          Ta bort
                        </Button>
                      </div>
                      
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                        {files[0].type.startsWith('image/') && (
                          <img
                            src={previewUrl}
                            alt="Preview"
                            style={{ width: '100%', maxHeight: '400px', objectFit: 'contain' }}
                          />
                        )}
                        {files[0].type === 'application/pdf' && (
                          <div style={{ height: '500px' }}>
                            <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                              <Viewer fileUrl={previewUrl} />
                            </Worker>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          )}
          {/* Info Panel - Shows on step 1 */}
          {currentStep === 1 && (
            <Col lg={4}>
              <div className="modern-card">
                <div className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <Info size={20} style={{ color: '#3b82f6' }} className="me-2" />
                    <h5 style={{ margin: 0, fontWeight: '600' }}>Så fungerar det</h5>
                  </div>
                  
                  <div className="mb-4">
                    <div className="d-flex align-items-start mb-3">
                      <Upload size={16} style={{ color: '#44814E' }} className="me-2 mt-1" />
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>1. Ladda upp dokument</div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          Släpp PDF eller bildfil med din leverantörsfaktura
                        </div>
                      </div>
                    </div>
                    
                    <div className="d-flex align-items-start mb-3">
                      <Brain size={16} style={{ color: '#44814E' }} className="me-2 mt-1" />
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>2. AI-analys</div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          Automatisk extraktion av leverantör, belopp och datum
                        </div>
                      </div>
                    </div>
                    
                    <div className="d-flex align-items-start mb-3">
                      <Eye size={16} style={{ color: '#44814E' }} className="me-2 mt-1" />
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>3. Granska & korrigera</div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          Kontrollera AI-resultatet och justera vid behov
                        </div>
                      </div>
                    </div>
                    
                    <div className="d-flex align-items-start">
                      <CheckCircle2 size={16} style={{ color: '#44814E' }} className="me-2 mt-1" />
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>4. Skapa faktura</div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          Slutför och spara i systemet för betalningshantering
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ background: '#f0f9ff', borderRadius: '8px', padding: '1rem' }}>
                    <div className="d-flex align-items-center mb-2">
                      <AlertTriangle size={16} style={{ color: '#0284c7' }} className="me-2" />
                      <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>Tips</div>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#0c4a6e' }}>
                      För bästa resultat, se till att dokumentet är tydligt och alla viktiga uppgifter syns väl.
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          )}

          {/* Step 2: Review OCR Results */}
          {currentStep === 2 && (
            <>
              <Col lg={6}>
                <div className="modern-card">
                  <div className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <Brain size={24} style={{ color: '#44814E' }} className="me-3" />
                      <div>
                        <h4 style={{ margin: 0, fontWeight: '600' }}>AI-extraherade uppgifter</h4>
                        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                          Granska och korrigera vid behov
                        </p>
                      </div>
                    </div>

                    {/* OCR Results Display */}
                    {ocrData && (
                      <div className="mb-4">
                        {/* Invoice Number */}
                        {ocrData.invoiceId && (
                          <div className="ocr-result-field">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <label style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                                <FileText size={16} className="me-2" />
                                Fakturanummer
                              </label>
                              <div 
                                className="confidence-badge"
                                style={{ 
                                  background: `${getConfidenceColor(ocrData.invoiceId.confidence)}15`,
                                  color: getConfidenceColor(ocrData.invoiceId.confidence)
                                }}
                              >
                                {(ocrData.invoiceId.confidence * 100).toFixed(0)}% säker
                              </div>
                            </div>
                            <input
                              type="text"
                              className="modern-input w-100"
                              value={invoiceNumber}
                              onChange={(e) => {
                                setInvoiceNumber(e.target.value);
                                validateField('invoiceNumber', e.target.value);
                              }}
                            />
                            {fieldValidation.invoiceNumber && !fieldValidation.invoiceNumber.isValid && (
                              <div className="field-validation" style={{ color: '#dc2626' }}>
                                {fieldValidation.invoiceNumber.message}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Amount */}
                        {ocrData.invoiceTotal && (
                          <div className="ocr-result-field">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <label style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                                <DollarSign size={16} className="me-2" />
                                Belopp (inkl. moms)
                              </label>
                              <div 
                                className="confidence-badge"
                                style={{ 
                                  background: `${getConfidenceColor(ocrData.invoiceTotal.confidence)}15`,
                                  color: getConfidenceColor(ocrData.invoiceTotal.confidence)
                                }}
                              >
                                {(ocrData.invoiceTotal.confidence * 100).toFixed(0)}% säker
                              </div>
                            </div>
                            <input
                              type="number"
                              className="modern-input w-100"
                              value={amount}
                              onChange={(e) => {
                                setAmount(e.target.value);
                                validateField('amount', e.target.value);
                              }}
                              min="0"
                              step="0.01"
                            />
                            {fieldValidation.amount && !fieldValidation.amount.isValid && (
                              <div className="field-validation" style={{ color: '#dc2626' }}>
                                {fieldValidation.amount.message}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Dates */}
                        <Row>
                          <Col md={6}>
                            <div className="ocr-result-field">
                              <label style={{ fontWeight: '600', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem' }}>
                                <Calendar size={16} className="me-2" />
                                Fakturadatum
                              </label>
                              <DatePicker
                                className="modern-input w-100"
                                selected={invoiceDate}
                                onChange={(date) => setInvoiceDate(date)}
                                dateFormat="yyyy-MM-dd"
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="ocr-result-field">
                              <label style={{ fontWeight: '600', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem' }}>
                                <Calendar size={16} className="me-2" />
                                Förfallodatum
                              </label>
                              <DatePicker
                                className="modern-input w-100"
                                selected={dueDate}
                                onChange={(date) => setDueDate(date)}
                                dateFormat="yyyy-MM-dd"
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* Supplier Selection */}
                    <div className="ocr-result-field">
                      <label style={{ fontWeight: '600', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem' }}>
                        <Building size={16} className="me-2" />
                        Leverantör
                      </label>

                      {/* Selected Supplier Confirmation */}
                      {selectedSupplier && suppliers.find(s => s.supplier_id === selectedSupplier) && (
                        <div style={{
                          background: '#ecfdf5',
                          border: '1px solid #059669',
                          borderRadius: '8px',
                          padding: '0.75rem',
                          marginBottom: '1rem'
                        }}>
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <CheckCircle2 size={16} style={{ color: '#059669' }} className="me-2" />
                              <div>
                                <div style={{ fontWeight: '500', fontSize: '0.875rem', color: '#059669' }}>
                                  ✓ {suppliers.find(s => s.supplier_id === selectedSupplier)?.name}
                                </div>
                                {suppliers.find(s => s.supplier_id === selectedSupplier)?.orgnumber && (
                                  <div style={{ fontSize: '0.75rem', color: '#065f46' }}>
                                    Org.nr: {suppliers.find(s => s.supplier_id === selectedSupplier)?.orgnumber}
                                  </div>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => {
                                setSelectedSupplier('');
                                setSupplierSuggestions([]);
                              }}
                              style={{ color: '#059669', textDecoration: 'none' }}
                            >
                              Ändra
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Supplier Suggestions - Only show if no supplier selected */}
                      {!selectedSupplier && supplierSuggestions.length > 0 && (
                        <div className="mb-3">
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                            Föreslagna leverantörer:
                          </div>
                          {supplierSuggestions.map(supplier => (
                            <div
                              key={supplier.supplier_id}
                              className="supplier-suggestion"
                              onClick={() => {
                                setSelectedSupplier(supplier.supplier_id);
                                setSupplierSuggestions([]);
                              }}
                            >
                              <div style={{ fontWeight: '500' }}>{supplier.name}</div>
                              {supplier.orgnumber && (
                                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                  Org.nr: {supplier.orgnumber}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Dropdown - Only show if no supplier selected */}
                      {!selectedSupplier && (
                        <>
                          <div className="d-flex gap-2">
                            <select
                              className="modern-input flex-grow-1"
                              value={selectedSupplier}
                              onChange={(e) => {
                                setSelectedSupplier(e.target.value);
                              }}
                            >
                              <option value="">Välj leverantör</option>
                              {suppliers.map((supplier) => (
                                <option key={supplier.supplier_id} value={supplier.supplier_id}>
                                  {supplier.name}
                                </option>
                              ))}
                            </select>
                            <Button
                              variant="outline-primary"
                              onClick={() => openNewSupplierModal(initialSupplierData)}
                              style={{ borderColor: '#44814E', color: '#44814E' }}
                            >
                              <Plus size={16} />
                            </Button>
                          </div>

                          {fieldValidation.supplier && !fieldValidation.supplier.isValid && (
                            <div className="field-validation" style={{ color: '#dc2626' }}>
                              {fieldValidation.supplier.message}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-between mt-4">
                      <Button 
                        variant="outline-secondary"
                        onClick={() => setCurrentStep(1)}
                      >
                        <ArrowLeft size={16} className="me-2" />
                        Tillbaka
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => setCurrentStep(3)}
                        disabled={!selectedSupplier || !invoiceNumber || !amount}
                        style={{ background: '#44814E', borderColor: '#44814E' }}
                      >
                        Fortsätt
                        <ArrowRight size={16} className="ms-2" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Document Preview */}
              <Col lg={6}>
                <div className="modern-card">
                  <div className="p-4">
                    <h5 style={{ fontWeight: '600', marginBottom: '1rem' }}>
                      <Eye size={20} className="me-2" />
                      Dokument
                    </h5>
                    {files.length > 0 && previewUrl && (
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                        {files[0].type.startsWith('image/') && (
                          <img
                            src={previewUrl}
                            alt="Preview"
                            style={{ width: '100%', maxHeight: '600px', objectFit: 'contain' }}
                          />
                        )}
                        {files[0].type === 'application/pdf' && (
                          <div style={{ height: '600px' }}>
                            <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                              <Viewer fileUrl={previewUrl} />
                            </Worker>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </>
          )}

          {/* Step 3: Final Review & Submit */}
          {currentStep === 3 && (
            <Col lg={8}>
              <div className="modern-card">
                <div className="p-4">
                  <div className="d-flex align-items-center mb-4">
                    <CheckCircle2 size={24} style={{ color: '#059669' }} className="me-3" />
                    <div>
                      <h4 style={{ margin: 0, fontWeight: '600' }}>Slutför leverantörsfaktura</h4>
                      <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                        Kontrollera alla uppgifter innan du skapar fakturan
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit}>
                    {/* Summary Display */}
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '1.5rem', marginBottom: '2rem' }}>
                      <Row>
                        <Col md={6}>
                          <div className="mb-3">
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              Leverantör
                            </div>
                            <div style={{ fontWeight: '600' }}>
                              {suppliers.find(s => s.supplier_id === selectedSupplier)?.name || 'Inte vald'}
                            </div>
                          </div>
                          <div className="mb-3">
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              Fakturanummer
                            </div>
                            <div style={{ fontWeight: '600' }}>{invoiceNumber}</div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              Belopp
                            </div>
                            <div style={{ fontWeight: '600', fontSize: '1.125rem', color: '#059669' }}>
                              {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(parseFloat(amount) || 0)}
                            </div>
                          </div>
                          <div className="mb-3">
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              Förfallodatum
                            </div>
                            <div style={{ fontWeight: '600' }}>{format(dueDate, 'yyyy-MM-dd')}</div>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    {/* Status Selection */}
                    <div className="mb-4">
                      <label style={{ fontWeight: '600', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem' }}>
                        Status
                      </label>
                      <select
                        className="modern-input"
                        style={{ width: '200px' }}
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="Scanned">Skannad</option>
                        <option value="Pending Approval">Väntar godkännande</option>
                        <option value="Approved">Godkänd</option>
                        <option value="Pending Payment">Väntar betalning</option>
                        <option value="Paid">Betald</option>
                      </select>
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-between">
                      <Button 
                        variant="outline-secondary"
                        onClick={() => setCurrentStep(2)}
                      >
                        <ArrowLeft size={16} className="me-2" />
                        Tillbaka
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isProcessing}
                        style={{ background: '#059669', borderColor: '#059669' }}
                      >
                        {isProcessing ? (
                          <>
                            <Loader2 size={16} className="me-2 spin" />
                            Skapar faktura...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 size={16} className="me-2" />
                            {isEditMode ? 'Uppdatera leverantörsfaktura' : 'Skapa leverantörsfaktura'}
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </Col>
          )}
        </Row>
          </>
        )}

        {/* Supplier Modal */}
        <NewSupplierModal
          key={modalKey}
          show={showNewSupplierModal}
          handleClose={handleClose}
          handleSave={handleSave}
          handleUpdate={handleSave}
          initialSupplierData={initialSupplierData}
        />
      </div>
    </>
  );
};

export default CreateNewSupplierInvoice;